import React, { useState, useEffect } from 'react';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CheckboxList from 'components/CheckboxList';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import Divider from 'components/Divider';
import Row from 'components/Row';
import Text from 'components/Text';
import MediaObject from 'components/MediaObject';
import ToastActions from 'actions/ToastActions';
import Select from 'components/Select';
import Icon from 'components/Icon';

import {
  getCoachCreateFields,
  getCoachStatusValues,
  updateCoach,
} from 'coaching/actions/CoachingActions';

import { Coaches_coaching_coaches_coaches } from 'coaching/queries/__generated__/Coaches';

export type Props = {
  selectedCoach?: Coaches_coaching_coaches_coaches;
  onHide: () => void;

  // we pass this into this component because we need to refetch the coach list
  // query with the exact same variables in order for the UI to update correctly
  // after calling the updateCoach mutation
  nameQuery: string;
};

export default function CoachingCoordinatorEditCoachSidebar(props: Props) {
  const { selectedCoach, onHide } = props;
  const [updatingExistingCoach, setUpdatingExistingCoach] = useState(false);
  const [coachStatus, setCoachStatus] = useState<string>(props.selectedCoach?.status || '');
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(
    props.selectedCoach?.subjects || []
  );
  const [subjectsOptions, setSubjectsOptions] = useState<string[]>([]);
  const [statusesOptions, setStatusesOptions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fechDataToEditCoach = async () => {
    setLoading(true);
    const statusesResult: { statuses?: string[]; error?: string } = await getCoachStatusValues();
    const coachCreateFieldsResult: {
      subjects?: string[];
      error?: string;
      hourlyRateInCents?: number[];
    } = await getCoachCreateFields();
    const error = statusesResult?.error || coachCreateFieldsResult.error;
    if (error) {
      console.error(
        'CoachingCoordinatorEditCoachSidebar - There was an error getting the data to edit the coach'
      );
      ToastActions.showErrorToast(error);
      setError(error);
    } else {
      setStatusesOptions(statusesResult?.statuses || []);
      setSubjectsOptions(coachCreateFieldsResult?.subjects || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fechDataToEditCoach();
    setCoachStatus(props.selectedCoach?.status || '');
    setSelectedSubjects(props.selectedCoach?.subjects || []);
  }, [props.selectedCoach]);

  if (error || !subjectsOptions || !statusesOptions) {
    return (
      <Row>
        <ErrorReporter error="Could not fetch coachable subjects" />
      </Row>
    );
  }

  if (loading) {
    return <CenteredLoadingIndicator />;
  }

  const handleUpdateCoach = async () => {
    setUpdatingExistingCoach(true);
    if (props.selectedCoach) {
      await updateCoach(props.selectedCoach.uuid, selectedSubjects, coachStatus, props.nameQuery);
      onHide();
      setUpdatingExistingCoach(false);
    }
  };

  const subjectOptions = new Map<string, string>(
    subjectsOptions.map(subject => [subject, subject])
  );
  const statusesOptionsSelect = statusesOptions.map(status => ({
    label: status,
    value: status,
  }));
  return (
    <BoxBody size="small">
      <Text align="right">
        <Button
          data-test="coach-sidebar-close-button"
          level="icon"
          color="chalkboard"
          onClick={onHide}>
          <Icon name="x" />
        </Button>
      </Text>
      <Divider size="xxsmall" />
      <Row>
        <Heading data-test="coach-name-text" level="2" serif>
          {selectedCoach?.name}
        </Heading>
        <Text
          data-test="coach-schedule-instance-student-data"
          element="div"
          weight="normal"
          size="xsmall"
          color="graphite">
          Coach
        </Text>
      </Row>
      <Divider size="medium" />
      <Row>
        <Heading level="5">Active Subject areas</Heading>
        <Text
          data-test="coach-schedule-instance-student-data"
          element="div"
          weight="normal"
          size="xsmall"
          color="graphite">
          {selectedSubjects?.join(', ')}
        </Text>
      </Row>
      <Row>
        <CheckboxList
          data-test="subject-checkbox-list"
          options={subjectOptions}
          onChange={(newSelectedSubjects: string[]) => setSelectedSubjects(newSelectedSubjects)}
          checked={selectedSubjects}
        />
      </Row>
      <Divider size="medium" />
      <Heading level="5">Status</Heading>
      <Row>
        <Select
          data-test="status-select-list"
          options={statusesOptionsSelect}
          onChange={(statusSelected: string) => setCoachStatus(statusSelected)}
          placeholder="Status"
          value={coachStatus}
        />
      </Row>
      <MediaObject center>
        <Button
          size="small"
          data-test="update-coach-button"
          loading={updatingExistingCoach}
          disabled={false}
          onClick={handleUpdateCoach}>
          Update
        </Button>
        <Button level="secondary" size="small" data-test="cancel-coach-button" onClick={onHide}>
          Cancel
        </Button>
      </MediaObject>
    </BoxBody>
  );
}
